
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { VALID_CONTENT_TYPES, INBOX_ID, ARCHIVE_ID } from "@/utils/contentFormUtils";
import { Collection } from "@/contexts/collections/types";
import { useTags, Tag } from "@/hooks/useTags";
import AddTagButton from "@/components/common/AddTagButton";
import { X } from "lucide-react";

interface UrlFieldProps {
  url: string;
  setUrl: (url: string) => void;
}

export const UrlField = ({ url, setUrl }: UrlFieldProps) => (
  <div className="space-y-2">
    <Label htmlFor="url">URL</Label>
    <Input
      id="url"
      type="url"
      placeholder="https://example.com"
      value={url}
      onChange={(e) => setUrl(e.target.value)}
      required
    />
    <p className="text-xs text-clustr-mediumGray">
      Enter the URL of the content you want to save
    </p>
  </div>
);

interface TitleFieldProps {
  title: string;
  setTitle: (title: string) => void;
}

export const TitleField = ({ title, setTitle }: TitleFieldProps) => (
  <div className="space-y-2">
    <Label htmlFor="title">Title</Label>
    <Input
      id="title"
      placeholder="Title of the content"
      value={title}
      onChange={(e) => setTitle(e.target.value)}
    />
    <p className="text-xs text-clustr-mediumGray">
      Leave blank to auto-extract from the URL
    </p>
  </div>
);

interface DescriptionFieldProps {
  description: string;
  setDescription: (description: string) => void;
}

export const DescriptionField = ({ description, setDescription }: DescriptionFieldProps) => (
  <div className="space-y-2">
    <Label htmlFor="description">Description (Optional)</Label>
    <Textarea
      id="description"
      placeholder="Add a description"
      value={description}
      onChange={(e) => setDescription(e.target.value)}
      rows={3}
    />
  </div>
);

interface ContentTypeFieldProps {
  contentType: string;
  setContentType: (contentType: string) => void;
}

export const ContentTypeField = ({ contentType, setContentType }: ContentTypeFieldProps) => (
  <div className="space-y-2">
    <Label htmlFor="content-type">Content Type</Label>
    <Select value={contentType} onValueChange={setContentType}>
      <SelectTrigger>
        <SelectValue placeholder="Select content type" />
      </SelectTrigger>
      <SelectContent>
        {VALID_CONTENT_TYPES.map(type => (
          <SelectItem key={type} value={type}>
            {type.charAt(0).toUpperCase() + type.slice(1)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
    <p className="text-xs text-clustr-mediumGray">
      Select a content type for better organization
    </p>
  </div>
);

interface CollectionFieldProps {
  selectedCollection: string;
  setSelectedCollection: (collection: string) => void;
  collections: Collection[];
}

export const CollectionField = ({ selectedCollection, setSelectedCollection, collections }: CollectionFieldProps) => (
  <div className="space-y-2">
    <Label htmlFor="collections">Collection</Label>
    <Select value={selectedCollection} onValueChange={setSelectedCollection}>
      <SelectTrigger>
        <SelectValue>
          {selectedCollection === INBOX_ID ? "In Box" : ""}
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={INBOX_ID}>In Box</SelectItem>
        {collections.map(collection => (
          <SelectItem key={collection.id} value={collection.id}>
            {collection.name}
          </SelectItem>
        ))}
        <SelectItem value={ARCHIVE_ID}>Archive</SelectItem>
      </SelectContent>
    </Select>
    <p className="text-xs text-clustr-mediumGray">
      Assign this content to one collection
    </p>
  </div>
);

interface TagFieldProps {
  selectedTags: string[];
  onTagToggle: (tagId: string) => void;
}

export const TagField = ({ selectedTags, onTagToggle }: TagFieldProps) => {
  const { tags } = useTags();

  const getSelectedTagObjects = () => {
    return tags.filter(tag => selectedTags.includes(tag.id));
  };

  const handleRemoveTag = (tagId: string) => {
    onTagToggle(tagId);
  };

  const handleTagToggle = (tagId: string) => {
    onTagToggle(tagId);
  };

  return (
    <div className="space-y-2">
      <Label htmlFor="tags">Tags (Optional)</Label>
      
      {/* Selected tags display */}
      {selectedTags.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {getSelectedTagObjects().map(tag => (
            <Badge 
              key={tag.id} 
              variant="secondary"
              className="flex items-center gap-1 px-2 py-1"
              style={{ backgroundColor: `${tag.color}20`, color: tag.color, borderColor: tag.color }}
            >
              {tag.name}
              <button
                type="button"
                onClick={() => handleRemoveTag(tag.id)}
                className="ml-1 hover:bg-black/10 rounded-sm p-0.5"
              >
                <X size={12} />
              </button>
            </Badge>
          ))}
        </div>
      )}
      
      {/* Add tag button */}
      <AddTagButton
        variant="outline"
        className="w-full justify-start"
        existingTags={getSelectedTagObjects()}
        onTagToggle={handleTagToggle}
        forceUseExternalDialog={false}
        preventPageReload={true}
      />
      
      <p className="text-xs text-clustr-mediumGray">
        Add tags to organize and categorize your content
      </p>
    </div>
  );
};
